import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_family-connect-195/artifacts/d7hehvno_Color%20logo%20with%20background.svg';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="terms-page">
      <header className="glass-header sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Kinsy" className="h-8" />
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-stone-600 hover:text-teal-600">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-4xl font-bold text-stone-900 tracking-tight mb-2">Terms of Service</h1>
        <p className="text-sm text-stone-500 mb-10">Last updated: February 19, 2026</p>

        <div className="text-stone-800 space-y-6 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">1. Agreement</h2>
            <p>By creating a Kinsy account you agree to these Terms with <strong>Kinsy LLC</strong> ("we"). If you don't agree, don't use the service.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">2. Your account</h2>
            <p>You must be at least 13 (16 in the EU/UK) to create an account. You're responsible for keeping your password secret and for any activity under your account.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">3. Family circles</h2>
            <p>The family admin (the creator of the family circle) controls invitations and member roles. By inviting someone you confirm you have permission to share their email with us. Anyone in the family can host events and edit family-tree details — by joining a family you trust the other members.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">4. Content you share</h2>
            <p>You keep ownership of everything you post. By posting, you grant Kinsy a worldwide, royalty-free license to host and display that content within your family circle (and to fulfillment partners like Gelato when you order a printed photo book). We will never sell, license or train AI on your content.</p>
            <p className="mt-2">You promise that your content (a) is yours to share, (b) doesn't infringe anyone's rights, and (c) isn't illegal, sexually explicit involving minors, or threatening.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">5. Acceptable use</h2>
            <p>Don't use Kinsy to harass, defraud, harvest data, run malware, or break the law. Family admins may report and hide content; repeated violations may result in account termination.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">6. Subscriptions &amp; billing</h2>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Free plan</strong>: 50 photos and up to 5 members per family.</li>
              <li><strong>Premium</strong>: $4.99/month — unlimited photos &amp; members for one family.</li>
              <li><strong>Family Premium</strong>: $9.99/month — Premium plus AI photo tagging, custom themes and a discount on physical keepsakes.</li>
            </ul>
            <p className="mt-2">Subscriptions auto-renew until cancelled. You can cancel at any time from the Stripe billing portal — your access continues through the end of the paid period. We do not provide pro-rated refunds for partial months but will honor refund requests for charges within 7 days at our discretion.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">7. Physical keepsakes (Gelato)</h2>
            <p>Photo books and other printed items are produced and shipped by Gelato. We do not refund printed items unless they arrive damaged or defective; in that case email <a className="text-teal-600 hover:underline" href="mailto:contact@kinsy.io">contact@kinsy.io</a> within 14 days with a photo and we'll replace the order.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">8. Termination</h2>
            <p>You can delete your account anytime from Profile → Danger zone. We may suspend or terminate accounts that violate these Terms. We'll give you a chance to export content unless prohibited by law.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">9. Disclaimers</h2>
            <p>Kinsy is provided "as is". We disclaim all implied warranties, including merchantability and fitness for a particular purpose. We don't guarantee uninterrupted service.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">10. Limitation of liability</h2>
            <p>To the fullest extent permitted by law, Kinsy LLC's total liability for any claim is capped at the amount you paid us in the 12 months before the claim, or $100, whichever is greater. We are not liable for indirect, incidental or consequential damages, or for lost data beyond what our backups can restore.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">11. Governing law</h2>
            <p>These Terms are governed by the laws of the United States, without regard to conflict-of-laws principles. Disputes will be resolved in the state and federal courts located in the United States, and you consent to that jurisdiction.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">12. Changes</h2>
            <p>We may update these Terms; we'll let you know in-app. Continued use after a change means you accept the update.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">13. Contact</h2>
            <p>
              Kinsy LLC<br />
              <a className="text-teal-600 hover:underline" href="mailto:contact@kinsy.io">contact@kinsy.io</a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
